import { useAutocompleteItems } from "@/hooks/useAutoCompleteItems";
import { useAutoLoadList } from "@/hooks/useAutoLoadList";
import { Autocomplete, AutocompleteItem, Button, Chip } from "@heroui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FieldValues,
  Path,
  PathValue,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { AutocompleteFilters, FiltersArg, FiltersBuilder } from "./types";

interface AutocompleteItemShape {
  id: number;
  text: string;
}

export interface AsyncAutocompleteFieldProps {
  name: string;
  label: string;
  watch: any;
  setValue: any;
  base_url: string;
  column_name?: string;
  id_column?: string;
  showEndContent?: boolean;
  showChip?: boolean;
  keepDuplicates?: boolean;
  required?: boolean;
  allowEmptyQuery?: boolean;
  minQueryLength?: number;
  filters?: FiltersArg;
}

export function AsyncAutocompleteField<T extends FieldValues>({
  name,
  label,
  watch,
  getValues,
  setValue,
  base_url,
  column_name,
  id_column,
  showEndContent = false,
  showChip = false,
  keepDuplicates = false,
  required = false,
  allowEmptyQuery = false,
  minQueryLength = 1,
  filters = {},
}: AsyncAutocompleteFieldProps & {
  watch: UseFormWatch<T>;
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
}) {
  const { t } = useTranslation();
  const translatedLabel = t(label, { defaultValue: label });

  const fieldValue = watch(name as Path<T>);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const isSelectionChange = useRef(false);

  const actualColumnName = column_name || name;
  const effectiveMinQueryLength = Math.max(1, minQueryLength);

  const resolvedFilters = useMemo<FiltersArg<T>>(() => {
    if (typeof filters === "function") {
      const builder = filters as FiltersBuilder<T>;
      return () => builder({ watch, getValues });
    }
    return filters as AutocompleteFilters;
  }, [filters, watch, getValues]);

  const {
    items: suggestions,
    hasMore,
    isLoading,
    onLoadMore,
    onQueryChange,
    cancelPending,
  } = useAutocompleteItems(base_url, actualColumnName, {
    allowEmptyQuery,
    keepDuplicates,
    filters: resolvedFilters,
  });

  const listboxRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleLoadMoreClick = async () => {
    if (isLoading || !hasMore) return;
    await onLoadMore();
    requestAnimationFrame(() => {
      const scroller = listboxRef.current;
      if (!scroller) return;

      const remaining =
        scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight);
      const stillAtBottom = remaining <= 4;

      if (stillAtBottom) {
        const nudge = Math.ceil(scroller.clientHeight * 0.33);
        scroller.scrollTop = Math.max(0, scroller.scrollTop - nudge);
      }
    });
  };

  useAutoLoadList({
    listboxRef,
    sentinelRef,
    isOpen,
    hasMore,
    isLoading,
    onLoadMore,
    deps: [suggestions.length],
  });

  const showBottomButton =
    (allowEmptyQuery ||
      (inputValue && inputValue.length >= effectiveMinQueryLength)) &&
    (hasMore || isLoading);

  useEffect(() => {
    const text =
      typeof fieldValue === "string"
        ? fieldValue
        : ((fieldValue as any)?.text ?? "");

    if ((text ?? "") !== (inputValue ?? "")) {
      isSelectionChange.current = true;
      setInputValue(text);
    }
  }, [fieldValue, inputValue]);

  const lastIssuedQueryRef = useRef<string | null>(null);

  const issueQuery = (q: string) => {
    const normalized = (q ?? "").trim();
    if (lastIssuedQueryRef.current === normalized) return;
    lastIssuedQueryRef.current = normalized;
    onQueryChange(normalized);
  };

  useEffect(() => {
    if (!isOpen) return;

    if (isSelectionChange.current) {
      isSelectionChange.current = false;
      return;
    }

    const timeout = window.setTimeout(() => {
      const q = (inputValue ?? "").trim();

      if (q.length === 0) {
        if (allowEmptyQuery) issueQuery("");
        return;
      }

      if (q.length >= effectiveMinQueryLength) {
        issueQuery(q);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [inputValue, isOpen, allowEmptyQuery, effectiveMinQueryLength]);

  const selectedKey = useMemo(() => {
    if (fieldValue && typeof fieldValue === "object" && fieldValue.id != null) {
      return String(fieldValue.id);
    }
    return undefined;
  }, [fieldValue]);

  const inputRef = useRef<HTMLButtonElement>(null);

  const emptyContent = isLoading
    ? t("autocomplete.loading")
    : allowEmptyQuery
      ? t("autocomplete.noResults")
      : inputValue && inputValue.length >= effectiveMinQueryLength
        ? t("autocomplete.noResults")
        : t("autocomplete.typeToSearch");

  const loadMoreLabel = isLoading
    ? t("autocomplete.loadingEllipsis")
    : hasMore
      ? t("autocomplete.loadMore")
      : "—";

  return (
    <Autocomplete
      label={translatedLabel}
      aria-label={translatedLabel}
      variant="bordered"
      size="sm"
      labelPlacement="outside"
      className="w-full"
      isLoading={isLoading}
      inputValue={inputValue}
      selectedKey={selectedKey}
      isClearable
      isRequired={required}
      scrollRef={listboxRef}
      selectorButtonProps={{ ref: inputRef }}
      listboxProps={{
        ref: listboxRef,
        classNames: {
          base: "max-h-80 overflow-auto",
        },
        emptyContent,
        bottomContent: showBottomButton ? (
          <div className="p-2 flex justify-center">
            <div ref={sentinelRef} className="flex justify-center w-full">
              <Button
                size="sm"
                variant="flat"
                className="w-full"
                isDisabled={isLoading || !hasMore}
                onPress={handleLoadMoreClick}
              >
                {loadMoreLabel}
              </Button>
            </div>
          </div>
        ) : null,
      }}
      onOpenChange={(open) => {
        setIsOpen(open);

        if (!open) {
          cancelPending();
          return;
        }

        const q = (inputValue ?? "").trim();
        if (q.length === 0) {
          if (allowEmptyQuery) issueQuery("");
          return;
        }

        // Do not force-query here; the debounced effect will handle it and dedupe.
      }}
      inputProps={{
        classNames: {
          inputWrapper:
            "bg-white hover:bg-white hover:border-slate-400 data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:border-primary border-2 shadow-sm relative",
          label: "text-blue-600 group-data-[filled-within=true]:text-blue-600",
        },
      }}
      popoverProps={{
        classNames: {
          content:
            "w-auto max-w-[var(--radix-popper-available-width)] !min-w-[210px]",
        },
        ref: listboxRef,
        shouldFlip: true,
        offset: 5,
      }}
      endContent={
        showEndContent && selectedKey ? (
          <Chip size="sm" variant="flat" color="default" className="mr-1">
            {selectedKey}
          </Chip>
        ) : null
      }
      allowsCustomValue
      onInputChange={(value) => {
        setInputValue(value);
        setValue(
          name as Path<T>,
          value ? { text: value } : (null as PathValue<T, Path<T>>),
        );
      }}
      onSelectionChange={(key) => {
        if (!key) return;

        const selectedItem = suggestions?.find(
          (item: AutocompleteItemShape) => item.id.toString() === key,
        );

        if (!selectedItem) return;

        isSelectionChange.current = true;

        setValue(
          name as Path<T>,
          {
            id: selectedItem.id,
            text: selectedItem.text,
          } as PathValue<T, Path<T>>,
        );

        if (id_column) {
          setValue(
            id_column as Path<T>,
            selectedItem.id as PathValue<T, Path<T>>,
          );
        }

        setInputValue(selectedItem.text ?? "");
      }}
      onClear={() => {
        setValue(name as Path<T>, null as PathValue<T, Path<T>>);
        if (id_column) {
          setValue(id_column as Path<T>, null as PathValue<T, Path<T>>);
        }
        setInputValue("");
      }}
    >
      <>
        {(inputValue ?? "").length > 0 && (
          <AutocompleteItem key="custom" textValue={inputValue}>
            <div className="flex items-center justify-between w-full whitespace-nowrap">
              <span className="truncate">{inputValue}</span>
              {showChip && (
                <Chip
                  size="sm"
                  variant="flat"
                  color="default"
                  className="ml-2 relative top-0 bottom-0 flex-shrink-0"
                >
                  {t("autocomplete.id", { id: 0 })}
                </Chip>
              )}
            </div>
          </AutocompleteItem>
        )}

        {suggestions?.map((item: AutocompleteItemShape) => (
          <AutocompleteItem key={item.id.toString()} textValue={item.text}>
            <div className="flex items-center justify-between w-full whitespace-nowrap">
              <span className="truncate">{item.text}</span>
              {showChip && (
                <Chip
                  size="sm"
                  variant="flat"
                  color="default"
                  className="ml-2 relative top-0 bottom-0 flex-shrink-0"
                >
                  {t("autocomplete.id", { id: item.id })}
                </Chip>
              )}
            </div>
          </AutocompleteItem>
        ))}
      </>
    </Autocomplete>
  );
}
